<template>
    <div class="container mortgage-calculator" :class="calculatorName">

		<div class="row justify-content-center calculator-header">
			<CalculatorHeader>
				<h2>Interest Only Mortgage Calculator</h2>				
				<h4>Fill out the few details below to see how we could help you achieve a mortgage</h4>
			</CalculatorHeader>
		</div>
		<div class="row justify-content-center calculator-body">
			<div class="col-md-10 col-xl-8 mb-3">
				<form class="calculator-container">

					<div class="mt-5" @keyup="resetCalculation" @change="resetCalculation">
					
						<BooleanChoiceButtons
							fieldText="Number of applicants"
							buttonTextLeft="One applicant"
							buttonTextRight="Two applicants"							
						>
							<template v-slot:left>
								<input type="radio" name="applicantCount" v-model="applicantsCount" :value="1" @click="clearApplicant2">																
							</template>
							<template v-slot:right>
								<input type="radio" name="applicantCount" v-model="applicantsCount" :value="2">																			
							</template>																
						</BooleanChoiceButtons>		
						
						<transition name="fade-x" mode="out-in">
							<div v-if="applicantsCount > 0">
								<h4 class="calc-fieldset-title">Applicant(s) Details</h4>							
								<InputElement
									fieldText="Applicant 1 annual income (including benefits)?"
								>
									<InputTypeNumber currencyPlaceholder v-model="annualIncome1" />
								</InputElement>

								<transition name="fade-x" mode="out-in">
									<InputElement
										v-if="applicantsCount === 2"
										fieldText="Applicant 2 annual income (including benefits)?"
									>
										<InputTypeNumber currencyPlaceholder v-model="annualIncome2" />
									</InputElement>							
								</transition>

								<ReturnedValueBox :innerElement=true text="Your household income: " :value="householdIncome" />								
								</div>											
						</transition>

						<transition name="fade-x" mode="out-in">
							<div v-if="sectionCompleted">
								
								<h4 class="mt-5 calc-fieldset-title">Property Details</h4>

								<InputElement
									fieldText="What is the property purchase price or valuation?"
								>
									<InputTypeNumber currencyPlaceholder v-model="housePrice" />
								</InputElement>								

								<transition name="fade-x" mode="out-in">
									<InputElement
										v-show="housePrice > 0"
										fieldText="How much do you need to borrow?"
									>
										<InputTypeNumber currencyPlaceholder v-model="amountToBorrow" />
									</InputElement>
								</transition>

								<transition name="fade-x" mode="out-in">
									<BooleanChoiceButtons
										v-show="housePrice > 0 && amountToBorrow > 0"
										fieldText="Do you have a repayment strategy?"
										buttonTextLeft="Yes"
										buttonTextRight="No"								
									>
										<template v-slot:left>
											<input type="radio" v-model="repaymentStrategy" value="Yes">							
										</template>
										<template v-slot:right>
											<input type="radio" v-model="repaymentStrategy" value="No">								
										</template>																
									</BooleanChoiceButtons>
								</transition>

								<transition name="scale-in" mode="out-in">
									<div
										v-if="sectionCompleted && housePrice > 0 && amountToBorrow > 0 && repaymentStrategy"
										class="text-center mt-5 mb-3"
									>
										<div class="btn calc-btn-secondary" @click="calculate">CALCULATE</div>
									</div>							
								</transition>

							</div>
						</transition>

					</div>

					<transition name="fade-y" mode="out-in">
						<div v-if="calcCompleted">

							<ResultsContainer class="mt-5 mb-5 calc-results-box">
								<div class="mb-3 results-title">You may be able to borrow up to:</div>
								<span class="fcs-price">£{{ formatNumberOutput(potentialLoanAmount) }}</span>
								<div class="mt-3">{{this.chance}}</div>
							</ResultsContainer>

							<SubmissionForm :calculatorName="calculatorName" :calculatorData="collateMessageBody" />

						</div>
					</transition>
				</form>
			</div>
		</div>

    </div>
</template>

<script>
import BooleanChoiceButtons from '@/components/utilities/booleanChoiceButtons.vue'
import CalculatorHeader from '@/components/utilities/calculatorHeader.vue'
import InputElement from '@/components/utilities/inputElement.vue'
import InputTypeNumber from '@/components/utilities/inputTypeNumber.vue'
import ResultsContainer from '@/components/utilities/resultsContainer.vue'
import ReturnedValueBox from '@/components/utilities/returnedValueBox.vue'
import SubmissionForm from '@/components/utilities/submissionForm.vue'

export default {
	components: {
		BooleanChoiceButtons,
		CalculatorHeader,
		InputElement,
		InputTypeNumber,
		ResultsContainer,
		ReturnedValueBox,
		SubmissionForm
	},	
    data() {
        return {
			calculatorName: 'interest-only-calculator',
            applicantsCount: null,
            annualIncome1: 0,
            annualIncome2: 0,
			housePrice: 0,
            amountToBorrow: null,
            repaymentStrategy: null,
			potentialLoanAmount: 0, 
			chance: '',
			calcCompleted: false
        }
    },
    methods: {
        calculate() {
			this.potentialLoanAmount = this.householdIncome * 4.85

			if( (this.loanToValue < 75) && (this.equity >= 100000) && (this.annualIncome1 >= 50000 || this.annualIncome2 >= 50000) ) {
				this.chance = `You've got a very good chance of getting an Interest Only Mortgage, contact us for further details`;
			}
			if( ((this.loanToValue < 75) && (this.equity >= 100000)) || (this.equity >= 100000 && (this.annualIncome1 >= 50000 || this.annualIncome2 >= 50000)) ) {
				this.chance = `You've got a good chance of getting an Interest Only Mortgage, contact us for further details.`;
			}
			if( (this.loanToValue < 75) || (this.equity >= 100000) ) {
				this.chance = `We may be able to get you an Interest Only Mortgage, contact us for further details.`;
			}
            if( (this.annualIncome1 >= 50000 || this.annualIncome2 >= 50000) || this.loanToValue > 75 ) {
				this.chance = `Please submit your details below to obtain your most suitable Interest Only mortgage advice and the most suitable rates.`;
            }
			this.calcCompleted = true;
        },
		formatNumberOutput(number) {
			const newNumber = (new Intl.NumberFormat('en-GB').format(number))
			if ( newNumber === 'NaN' ) { return 0 } return newNumber
		},		
        removeHyphensToUpperCase(str) {
            return str.replace(/(-|^)([^-]?)/g, function(_, prep, letter) { return (prep && ' ') + letter.toUpperCase(); });
		},
		resetCalculation() {
			this.calcCompleted = false;
		},
		clearApplicant2() {
			this.annualIncome2 = ''
		},
		formatEmptyMessageFields(message){
			return message ? message : 'N/A'
		}		
    },	
    computed: {
		/**
		 * Check if previous logical section is completed
		 * @return {Bool}
		 */
        sectionCompleted() {
			let sectionCompleted
			if ( !this.applicantsCount ) { sectionCompleted = false }
			this.applicantsCount == 2
				? ( this.annualIncome1 > 0 && this.annualIncome2 > 0 ) ? sectionCompleted = true : sectionCompleted = false
				: this.annualIncome1 > 0 ? sectionCompleted = true : sectionCompleted = false
			return sectionCompleted
		},		
		/**
		 * Calculate the total income for the household
		 * @return {Number}
		 */			
        householdIncome() {
            let annualIncome1 = this.annualIncome1 ? this.annualIncome1 : 0;
            let annualIncome2 = this.annualIncome2 ? this.annualIncome2 : 0;

            return annualIncome1 + annualIncome2;
        },
		equity() {
            let borrowAmount = this.amountToBorrow ? this.amountToBorrow : 0;
            let housePrice = this.housePrice ? this.housePrice : 0;
            return housePrice - borrowAmount;
        },
        loanToValue() {
            let borrowAmount = this.amountToBorrow ? this.amountToBorrow : 0;
            let housePrice = this.housePrice ? this.housePrice : 0;
            return (borrowAmount / housePrice) * 100;
        },
		collateMessageBody() {
			let calculatorData = {
				calculatorTitle: this.removeHyphensToUpperCase(this.calculatorName),
				calculatorResults:
				`
					Number of applicants: ${this.applicantsCount}
					Applicant 1 income: £${this.annualIncome1}
					Applicant 2 income: £${this.formatEmptyMessageFields(this.annualIncome2)}
					Household income: £${this.householdIncome}
					
					Property value: £${this.housePrice}
					How much do you need to borrow? £${this.amountToBorrow}
                    Do you have a repayment strategy? ${this.repaymentStrategy}
					Potentinal value of loan: £${this.potentialLoanAmount}
					Loan to value: £${this.loanToValue}
                    Equity available: £${this.equity}
				`
			}
			
			return calculatorData;
		}		
    }
}
</script>

<style lang="scss">
	@import './styles/calcs.scss';		
</style>